/**
 * Strongly typed calendar event item returned from the CalenderEvents API.
 */
export interface CalenderEventItem {
    id: number;
    title: string;
    description: string;
    needsRegister: boolean;
    keywords: string;
    startDate: string;
    endDate: string;
    price: number;
    eventUrl1: string;
    eventUrl2: string;
    eventUrl3: string;
    country: string;
    city: string;
    //Country TODO
}

const CALENDER_EVENTS_API_URL = 'https://smartdataproxy.azurewebsites.net/api/CalenderEvents';

/**
 * Singleton data manager for calendar events.
 * Caches fetched events per year/month; the REST endpoint is only queried when
 * the cache for that year/month is empty.
 */
export class CalenderEvents {
    private static instance: CalenderEvents;

    /** Cache key: "year-month", value: events array. */
    private itemsCache: Map<string, CalenderEventItem[]> = new Map();

    private constructor() {}

    static getInstance(): CalenderEvents {
        if (!CalenderEvents.instance) {
            CalenderEvents.instance = new CalenderEvents();
        }
        return CalenderEvents.instance;
    }

    private cacheKey(year: number, month: number): string {
        return `${year}-${month}`;
    }

    /**
     * Gets the latest calendar events for the given year and month.
     * Queries the REST endpoint only if the cache for that year/month is empty;
     * otherwise returns the saved items.
     */
    async GetLatestEvents(year: number, month: number): Promise<CalenderEventItem[]> {
        const key = this.cacheKey(year, month);
        if (this.itemsCache.has(key)) {
            return this.itemsCache.get(key)!;
        }

        const params = new URLSearchParams();
        params.set('year', String(year));
        params.set('month', String(month));
        const url = `${CALENDER_EVENTS_API_URL}?${params.toString()}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`CalenderEvents API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const items = Array.isArray(data) ? (data as CalenderEventItem[]) : [];
        this.itemsCache.set(key, items);
        return items;
    }

    /**
     * Gets the next N upcoming events starting from the start of the current year.
     * Fetches up to 6 months ahead to find enough events.
     */
    async GetUpcomingEvents(count: number = 10): Promise<CalenderEventItem[]> {
        const now = new Date();
        const startYear = now.getFullYear();
        const startMonth = now.getMonth() + 1;
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

        const all: CalenderEventItem[] = [];
        for (let i = 0; i < 6; i++) {
            let y = startYear;
            let m = startMonth + i;
            while (m > 12) {
                m -= 12;
                y += 1;
            }
            const items = await this.GetLatestEvents(y, m);
            all.push(...items);
        }

        const upcoming = all
            .filter((item) => new Date(item.startDate).getTime() >= today)
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
            .slice(0, count);

        return upcoming;
    }
}
